import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import StreakShare from "./StreakShare";
import StreakConfetti from "./StreakConfetti";

const formatNumber = (n) => Number(n || 0).toLocaleString("en-US");

// Frontend origin (same site the user is on) — used for the share link.
const frontendBase = () => {
  try {
    return `${window.location.origin}/`;
  } catch (e) {
    return "https://coin.spinsagacasino.com/";
  }
};

// Overlay shown OVER the streak modal when a streak is completed.
// Blurs/dims the modal behind and shows the totals (Coins / Bonus / Free Spins)
// + social share. Actions: Share (frontend-only) and Start New Streak (reset).
const StreakCelebration = ({
  open = false,
  streakDays = 7,
  totalSc = 0,
  totalGc = 0,
  totalFreeSpin = 0,
  starting = false,
  onStartNew,
  onClose,
}) => {
  const userId = useSelector((state) => state.auth?.IsLoginData?.idUser);
  const { t } = useTranslation();
  if (!open) return null;

  const rewards = [
    { label: t('streak.celebrationCash'), value: `${totalSc}`, color: "#ff7a00" },
    { label: t('streak.celebrationBonus'), value: `${formatNumber(totalGc)}`, color: "#c47f00" },
    { label: t('streak.celebrationFreeSpins'), value: `${totalFreeSpin}`, color: "#9d6bff" },
  ];

  // Dynamic referral-style link (attributes the share to this user).
  const shareLink = `${frontendBase()}?ref=${userId || ""}&campaign=streakWin`;
  const shareText = t('streak.celebrationShareText', {
    sc: totalSc,
    gc: formatNumber(totalGc),
    spins: totalFreeSpin,
    days: streakDays,
  });

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: "rgba(8,10,18,0.55)",
        backdropFilter: "blur(7px)",
        WebkitBackdropFilter: "blur(7px)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 460,
          borderRadius: 4,
          p: { xs: 2.5, sm: 3 },
          background: "#fff",
          boxShadow: "0 30px 70px -20px rgba(0,0,0,0.7)",
          overflow: "hidden",
        }}
      >
        <StreakConfetti />

        {/* Celebration header */}
        <Box
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 2.5,
            textAlign: "center",
            background: "linear-gradient(135deg, #d7f5e3, #b9efce)",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              mx: "auto",
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#34c171",
              color: "#fff",
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography
            sx={{
              color: "#1f7a48",
              fontWeight: 700,
              fontSize: "12px",
              letterSpacing: "1px",
            }}
          >
            {t('streak.celebrationDaysComplete', { days: streakDays })}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "26px", color: "#0f3d24" }}>
            {t('streak.celebrationHeading')}
          </Typography>
          <Typography sx={{ color: "#2e7d50", fontSize: "13px" }}>
            {t('streak.celebrationSubtitle', { days: streakDays })}
          </Typography>
        </Box>

        {/* Reward totals */}
        <Grid container spacing={1.5}>
          {rewards.map((r) => (
            <Grid item xs={4} key={r.label}>
              <Box
                sx={{
                  textAlign: "center",
                  p: { xs: 1.25, sm: 1.75 },
                  borderRadius: 2.5,
                  background: "#f6f7fb",
                  border: "1px solid #ececf2",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{ color: r.color, fontWeight: 800, fontSize: { xs: "15px", sm: "18px" } }}
                >
                  {r.value}
                </Typography>
                <Typography sx={{ color: "#888", fontSize: "11px", mt: 0.5 }}>
                  {r.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Social share (frontend-only) */}
        <Box
          sx={{
            background: "#11141c",
            borderRadius: 3,
            p: 2,
            mb: 2,
          }}
        >
          <Typography
            sx={{ color: "#fff", textAlign: "center", fontSize: "13px", mb: 1.5 }}
          >
            {t('streak.celebrationShareTitle')}
          </Typography>
          <StreakShare url={shareLink} title={shareText} />
        </Box>

        {/* Start New Streak (resets to day 1 + prompts plan pick) */}
        <Button
          fullWidth
          disabled={starting}
          onClick={onStartNew}
          sx={{
            borderRadius: 2.5,
            py: 1.2,
            fontWeight: 800,
            textTransform: "none",
            fontSize: "15px",
            color: "#fff",
            background: "linear-gradient(135deg, #fa114f, #c4003a)",
            "&:hover": { background: "linear-gradient(135deg, #ff2f64, #d40040)" },
          }}
        >
          {starting ? t('streak.celebrationStarting') : t('streak.celebrationStartNew')}
        </Button>

        <Button
          fullWidth
          onClick={onClose}
          sx={{
            mt: 1.25,
            py: 1,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: "13px",
            textTransform: "none",
            color: "#444",
            background: "#eceef3",
            border: "1px solid #d9dce3",
            "&:hover": { background: "#e0e3ea" },
          }}
        >
          {t('streak.celebrationLater')}
        </Button>
      </Box>
    </Box>
  );
};

export default StreakCelebration;
